import React from "react";
import * as Chakra from "@chakra-ui/core";
import {CSSReset, IUseColorMode} from "@chakra-ui/core";
import {Global, css} from "@emotion/core";

import {ClientTenant} from "~/tenant/types";
import {getColorModeVariables} from "~/theme";

export type ColorMode = IUseColorMode["colorMode"];

// Chakra v0.8 exports the context at runtime but not in its typings. Its own
// ColorModeProvider only reads `value` as initial state, so we provide the
// context ourselves to combine the tenant default with the buyer override
const ColorModeContext = (Chakra as any).ColorModeContext as React.Context<IUseColorMode>;

// Base styles read the same variables in both modes, so they are right before hydration
const resetConfig = () => {
  const config = {
    color: "var(--text)",
    bg: "var(--page)",
    borderColor: "var(--border-strong)",
    placeholderColor: "var(--faint)",
  };

  return {light: config, dark: config};
};

interface Props {
  defaultValue: ColorMode;
  storageKey: string;
  color: ClientTenant["color"];
}

export const ColorModeProvider: React.FC<Props> = ({children, defaultValue, storageKey, color}) => {
  const [colorMode, setColorMode] = React.useState<ColorMode>(defaultValue);

  // The blocking script below already applied the buyer override to <html>
  React.useEffect(() => {
    if (document.documentElement.classList.contains("dark")) setColorMode("dark");
    else setColorMode("light");
  }, []);

  React.useEffect(() => {
    document.documentElement.classList.toggle("dark", colorMode === "dark");
  }, [colorMode]);

  React.useEffect(() => () => document.documentElement.classList.remove("dark"), []);

  const toggleColorMode = React.useCallback(() => {
    const next: ColorMode = colorMode === "dark" ? "light" : "dark";

    setColorMode(next);

    try {
      window.localStorage.setItem(storageKey, next);
    } catch (error) {
      // Storage can be unavailable (private mode, WebViews)
    }
  }, [colorMode, storageKey]);

  const value = React.useMemo(() => ({colorMode, toggleColorMode}), [colorMode, toggleColorMode]);

  // Runs while the HTML is parsed, so the page paints in the right mode without flashing
  const script = `(function(){var m;try{m=localStorage.getItem(${JSON.stringify(
    storageKey,
  )})}catch(e){}var d=m==="dark"||(m!=="light"&&${JSON.stringify(
    defaultValue,
  )}==="dark");document.documentElement.classList.toggle("dark",d)})()`;

  return (
    <ColorModeContext.Provider value={value}>
      <script dangerouslySetInnerHTML={{__html: script}} />
      <Global
        styles={css`
          html {
            ${getColorModeVariables("light", color)}
          }

          html.dark {
            ${getColorModeVariables("dark", color)}
          }
        `}
      />
      <CSSReset config={resetConfig} />
      {children}
    </ColorModeContext.Provider>
  );
};

// In dark mode Chakra paints `variantColor` controls (buttons, steppers, radios) with the
// pale .200 shade and dark text. Its light styles (brand .500 with white text) already work on
// our dark surfaces, since grays and tokens switch through CSS variables
// (Chakra's LightMode is avoided because it also runs `use-dark-mode`, which writes to body/localStorage)
export const BrandColors: React.FC = ({children}) => {
  const parent = React.useContext(ColorModeContext);
  const value = React.useMemo(() => ({...parent, colorMode: "light" as ColorMode}), [parent]);

  return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>;
};
